// In-app notification center — bell icon with unread badge,
// dropdown panel listing recent notifications, mark-read actions,
// link to /settings#notifications for prefs.
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Bell, Check, CheckCheck, X, AlertTriangle, AlertCircle, Info, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SEV_ICON = {
  info: { Icon: Info, cls: 'text-[#5790C9]' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-400' },
  critical: { Icon: AlertCircle, cls: 'text-red-400' },
};

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const fetchCount = async () => {
    try { const r = await api.get('/notifications/unread-count'); setUnread(r.data.count || 0); }
    catch {}
  };

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await api.get('/notifications', { params: { limit: 30 } }); setItems(r.data.items || []); setUnread(r.data.unread_count || 0); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCount(); const t = setInterval(fetchCount, 60000); return () => clearInterval(t); }, []);
  useEffect(() => { if (open) fetchAll(); }, [open]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(arr => arr.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnread(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAll = async () => {
    try { await api.post('/notifications/read-all'); setItems(arr => arr.map(n => ({ ...n, read: true }))); setUnread(0); }
    catch {}
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.notification_id);
    if (n.link) {
      setOpen(false);
      navigate(n.link.startsWith('/') ? n.link : `/${n.link}`);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="notif-bell"
        className="relative p-2 rounded-md hover:bg-slate-800/40 transition" aria-label="Notifications">
        <Bell className="w-4 h-4" style={{ color: 'var(--text-muted, #9AA2B1)' }} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F26B1F] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 top-full mt-2 w-[380px] max-w-[90vw] z-50 rounded-xl shadow-2xl"
            style={{ backgroundColor: 'var(--card-bg, #0B1220)', border: '1px solid var(--border-color, rgba(30,78,140,0.4))' }}
            data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-color, rgba(30,78,140,0.4))' }}>
              <div>
                <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary, #E5E7EB)' }}>Notifications</h3>
                <p className="text-[10px] tracking-[0.2em] uppercase mt-0.5" style={{ color: 'var(--text-muted, #6B7280)' }}>
                  {unread > 0 ? `${unread} unread` : 'All caught up'}
                </p>
              </div>
              {unread > 0 && (
                <button onClick={markAll} data-testid="notif-mark-all" className="inline-flex items-center gap-1 text-[11px] tracking-wider text-[#5790C9] hover:text-[#F26B1F] transition">
                  <CheckCheck className="w-3.5 h-3.5"/> Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin opacity-60"/></div>
              ) : items.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <Bell className="w-8 h-8 mx-auto mb-3 opacity-30" style={{ color: 'var(--text-muted)' }}/>
                  <p className="text-sm" style={{ color: 'var(--text-muted, #9AA2B1)' }}>No notifications yet.</p>
                  <p className="text-xs mt-1 opacity-70" style={{ color: 'var(--text-muted)' }}>You'll see project updates, alerts, and system events here.</p>
                </div>
              ) : (
                <ul>
                  {items.map(n => {
                    const sev = SEV_ICON[n.severity] || SEV_ICON.info;
                    const SIcon = sev.Icon;
                    return (
                      <li key={n.notification_id}>
                        <button onClick={() => handleClick(n)} className="w-full text-left px-4 py-3 transition flex items-start gap-3 group hover:bg-slate-800/30"
                          style={{ borderBottom: '1px solid var(--border-color, rgba(30,78,140,0.2))' }}
                          data-testid={`notif-item-${n.notification_id}`}>
                          {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F26B1F] shrink-0"/>}
                          {n.read && <span className="mt-1.5 w-1.5 h-1.5 shrink-0"/>}
                          <SIcon className={`w-4 h-4 shrink-0 mt-0.5 ${sev.cls}`}/>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--text-muted, #6B7280)' }}>{n.category}</span>
                              <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted, #6B7280)' }}>{(n.created_at || '').slice(11, 16)}</span>
                            </div>
                            <div className={`text-[13px] truncate ${n.read ? '' : 'font-medium'}`} style={{ color: 'var(--text-primary, #E5E7EB)' }}>
                              {n.title}
                            </div>
                            <div className="text-[12px] line-clamp-2 mt-0.5" style={{ color: 'var(--text-muted, #9AA2B1)' }}>{n.message}</div>
                          </div>
                          {!n.read && (
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); markRead(n.notification_id); }}>
                              <Check className="w-3.5 h-3.5 text-slate-500 hover:text-slate-200"/>
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="px-4 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color, rgba(30,78,140,0.4))' }}>
              <button onClick={() => { setOpen(false); navigate('/settings/notifications'); }} className="text-[11px] tracking-wider text-slate-400 hover:text-slate-100 transition" data-testid="notif-open-inbox">
                View all notifications
              </button>
              <button onClick={() => { setOpen(false); navigate('/settings/notifications?tab=preferences'); }} className="text-[11px] tracking-wider text-slate-400 hover:text-slate-100 transition" data-testid="notif-open-prefs">
                Preferences
              </button>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200">
                <X className="w-3.5 h-3.5"/>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
